import { memo, useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  reduceMarkList,
  toActiveIndex,
  toMarkKeyCommand,
  toTreeCommand,
  type MarkListItem,
  type MarkListState,
  type MarkTreeItem,
  type Typeahead,
} from './markNavigation';

export type MarkListColors = {
  surface: string;
  ink: string;
  mutedInk: string;
  border: string;
};

export type MarkListClientPoint = { clientX: number; clientY: number };

type MarkListCommonProps = {
  /** The list's accessible name and visible heading, worded by the host. */
  label: string;
  activeId: string | null;
  onActiveChange: (id: string | null) => void;
  /** Enter, Space or a click on a row. */
  onActivate: (id: string) => void;
  /** The context-menu key, Shift+F10 or a secondary click on a row. */
  onContextMenu?: (id: string, at: MarkListClientPoint) => void;
  colors: MarkListColors;
};

type MarkListStructureProps =
  | { kind: 'list'; items: readonly MarkListItem[] }
  | {
      kind: 'tree';
      items: readonly MarkTreeItem[];
      onExpand: (id: string) => void;
      onCollapse: (id: string) => void;
    };

export type MarkListProps = MarkListCommonProps & MarkListStructureProps;

const TREE_INDENT = 14;

const VISUALLY_HIDDEN: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const toPanelStyle = ({ surface, ink, border }: MarkListColors) =>
  ({
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    width: 'min(360px, calc(100% - 16px))',
    maxHeight: 'calc(100% - 16px)',
    borderRadius: 6,
    border: `1px solid ${border}`,
    background: surface,
    color: ink,
    font: '12px system-ui, -apple-system, "Segoe UI", sans-serif',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
    outline: `2px solid ${ink}`,
    outlineOffset: -2,
  }) satisfies React.CSSProperties;

const toOptionId = (prefix: string, index: number): string =>
  `${prefix}-mark-${index}`;

const toAccessibleName = ({ label, details }: MarkListItem): string =>
  [label, ...details].join(', ');

const isTreeItem = (item: MarkListItem): item is MarkTreeItem =>
  'level' in item;

const toExpandedAttribute = (item: MarkListItem): boolean | undefined => {
  if (!isTreeItem(item) || item.expansion === 'leaf') return undefined;
  return item.expansion === 'expanded';
};

const toDisclosureGlyph = (item: MarkTreeItem): string => {
  if (item.expansion === 'leaf') return '';
  return item.expansion === 'expanded' ? '▾' : '▸';
};

type MarkRowProps = {
  item: MarkListItem;
  optionId: string;
  isActive: boolean;
  role: 'option' | 'treeitem';
  colors: MarkListColors;
  onRowClick: (id: string) => void;
  onRowDoubleClick: (id: string) => void;
  onRowContextMenu: (id: string, event: React.MouseEvent) => void;
};

const MarkRow = memo(
  ({
    item,
    optionId,
    isActive,
    role,
    colors,
    onRowClick,
    onRowDoubleClick,
    onRowContextMenu,
  }: MarkRowProps) => {
    const tree = isTreeItem(item) ? item : null;
    return (
      <div
        id={optionId}
        role={role}
        aria-label={toAccessibleName(item)}
        aria-selected={isActive}
        aria-expanded={toExpandedAttribute(item)}
        aria-level={tree?.level}
        aria-setsize={tree?.setSize}
        aria-posinset={tree?.posInSet}
        onClick={() => onRowClick(item.id)}
        onDoubleClick={() => onRowDoubleClick(item.id)}
        onContextMenu={event => onRowContextMenu(item.id, event)}
        style={{
          display: 'flex',
          gap: 6,
          padding: '4px 8px',
          paddingLeft: 8 + (tree ? (tree.level - 1) * TREE_INDENT : 0),
          cursor: 'default',
          background: isActive ? colors.border : 'transparent',
          outline: isActive ? `2px solid ${colors.ink}` : 'none',
          outlineOffset: -2,
        }}
      >
        {tree && (
          <span aria-hidden style={{ width: 10, flex: 'none' }}>
            {toDisclosureGlyph(tree)}
          </span>
        )}
        <span style={{ minWidth: 0 }}>
          <strong>{item.label}</strong>
          {item.details.map((detail, index) => (
            <span
              key={index}
              style={{
                display: 'block',
                color: isActive ? colors.ink : colors.mutedInk,
              }}
            >
              {detail}
            </span>
          ))}
        </span>
      </div>
    );
  }
);

/**
 * A focusable list of a canvas chart's marks, for keyboard and screen-reader readers.
 *
 * One tab stop: the rows are reached through `aria-activedescendant`. Visually hidden until it
 * receives focus, then drawn as a panel over the chart's top-right corner. Render it after the
 * canvas so it follows the canvas in the tab order.
 */
export const MarkList = memo((props: MarkListProps) => {
  const { label, activeId, onActiveChange, onActivate, onContextMenu, colors } =
    props;
  const prefix = useId();
  const labelId = `${prefix}-label`;
  const [isFocused, setIsFocused] = useState(false);
  const typeahead = useRef<Typeahead | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const items: readonly MarkListItem[] = props.items;
  const activeIndex = toActiveIndex(items, activeId);
  const activeOptionId =
    activeIndex === -1 ? undefined : toOptionId(prefix, activeIndex);

  useEffect(() => {
    if (!isFocused || activeOptionId === undefined) return;
    document.getElementById(activeOptionId)?.scrollIntoView?.({
      block: 'nearest',
    });
  }, [isFocused, activeOptionId]);

  const openContextMenu = (id: string, at: MarkListClientPoint) =>
    onContextMenu?.(id, at);

  const rowHandlers = useRef({
    onActiveChange,
    onActivate,
    onContextMenu,
    kind: props.kind,
  });
  rowHandlers.current = {
    onActiveChange,
    onActivate,
    onContextMenu,
    kind: props.kind,
  };

  const onRowClick = useCallback((id: string) => {
    rowHandlers.current.onActiveChange(id);
    if (rowHandlers.current.kind === 'tree') return;
    rowHandlers.current.onActivate(id);
  }, []);

  const onRowDoubleClick = useCallback((id: string) => {
    if (rowHandlers.current.kind !== 'tree') return;
    rowHandlers.current.onActivate(id);
  }, []);

  const onRowContextMenu = useCallback(
    (id: string, event: React.MouseEvent) => {
      const handlers = rowHandlers.current;
      if (!handlers.onContextMenu) return;
      event.preventDefault();
      event.stopPropagation();
      handlers.onActiveChange(id);
      handlers.onContextMenu(id, {
        clientX: event.clientX,
        clientY: event.clientY,
      });
    },
    []
  );

  const openActiveContextMenu = () => {
    if (activeId === null || activeOptionId === undefined) return;
    const bounds = document
      .getElementById(activeOptionId)
      ?.getBoundingClientRect();
    openContextMenu(activeId, {
      clientX: bounds?.left ?? 0,
      clientY: bounds?.bottom ?? 0,
    });
  };

  const runTreeCommand = (key: 'ArrowLeft' | 'ArrowRight') => {
    if (props.kind !== 'tree') return;
    const command = toTreeCommand(props.items, activeId, key);
    if (command === null) return;
    if (command.type === 'move') onActiveChange(command.id);
    if (command.type === 'expand') props.onExpand(command.id);
    if (command.type === 'collapse') props.onCollapse(command.id);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const command = toMarkKeyCommand(event, typeahead.current);
    if (command === null) return;

    switch (command.type) {
      case 'navigate': {
        const state: MarkListState = {
          activeId,
          typeahead: typeahead.current,
        };
        const next = reduceMarkList(state, items, command.navigation);
        typeahead.current = next.typeahead;
        onActiveChange(next.activeId);
        break;
      }
      case 'activate':
        if (activeId === null) return;
        onActivate(activeId);
        break;
      case 'clear':
        if (activeId === null) return;
        onActiveChange(null);
        break;
      case 'contextMenu':
        if (!onContextMenu) return;
        openActiveContextMenu();
        break;
      case 'tree':
        if (props.kind !== 'tree') return;
        runTreeCommand(command.key);
        break;
    }
    event.preventDefault();
  };

  const onFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.target !== listRef.current) return;
    setIsFocused(true);
    if (activeIndex === -1 && items.length > 0) onActiveChange(items[0].id);
  };

  const onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.target !== listRef.current) return;
    setIsFocused(false);
  };

  const onListContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!onContextMenu) return;
    event.preventDefault();
  };

  const role = props.kind === 'tree' ? 'treeitem' : 'option';

  return (
    <div style={isFocused ? toPanelStyle(colors) : VISUALLY_HIDDEN}>
      <div
        id={labelId}
        aria-hidden
        style={{
          padding: '6px 8px',
          fontWeight: 600,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        {label}
      </div>
      <div
        ref={listRef}
        role={props.kind === 'tree' ? 'tree' : 'listbox'}
        tabIndex={0}
        aria-labelledby={labelId}
        aria-activedescendant={activeOptionId}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        onContextMenu={onListContextMenu}
        style={{ overflowY: 'auto', minHeight: 0, outline: 'none' }}
      >
        {items.map((item, index) => (
          <MarkRow
            key={item.id}
            item={item}
            optionId={toOptionId(prefix, index)}
            isActive={index === activeIndex}
            role={role}
            colors={colors}
            onRowClick={onRowClick}
            onRowDoubleClick={onRowDoubleClick}
            onRowContextMenu={onRowContextMenu}
          />
        ))}
      </div>
    </div>
  );
});
